import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { supabase } from '../supabase';
import './Products.css';

function Products() {
  const { addToCart } = useCart();
  const { currency } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();

  // Function to get the correct price based on selected currency
  const getPriceForCurrency = (product, priceType = 'current') => {
    if (currency === '$') {
      return priceType === 'current' ? (product.price_usd || 0) : (product.original_price_usd || 0);
    } else if (currency === '£') {
      return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
    } else if (currency === 'AED') {
      return priceType === 'current' ? (product.price_aed || 0) : (product.original_price_aed || 0);
    } else if (currency === 'KSA') {
      return priceType === 'current' ? (product.price_sar || 0) : (product.original_price_sar || 0);
    }
    // Default fallback to GBP
    return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
  };

  // Function to get currency symbol
  const getCurrencySymbol = () => {
    if (currency === '$') return '$';
    else if (currency === '£') return '£';
    else if (currency === 'AED') return 'د.إ';
    else if (currency === 'KSA') return 'ر.س';
    return '£'; // Default fallback
  };
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('featured');

  // Handle URL parameters for category filtering and search
  useEffect(() => {
    // Scroll to top when URL parameters change
    window.scrollTo(0, 0);
    
    const searchParams = new URLSearchParams(location.search);
    const categoryFromUrl = searchParams.get('category');
    const subcategoryFromUrl = searchParams.get('subcategory');
    const searchFromUrl = searchParams.get('search');
    
    if (categoryFromUrl) {
      // Decode URL-encoded category names
      const decodedCategory = decodeURIComponent(categoryFromUrl);
      console.log('Category from URL:', categoryFromUrl, 'Decoded:', decodedCategory);
      setSelectedCategory(decodedCategory);
    }
    
    if (subcategoryFromUrl) {
      // Decode URL-encoded subcategory names
      const decodedSubcategory = decodeURIComponent(subcategoryFromUrl);
      console.log('Subcategory from URL:', subcategoryFromUrl, 'Decoded:', decodedSubcategory);
      setSelectedSubcategory(decodedSubcategory);
    }
    
    if (searchFromUrl) {
      // Filter products by search query
      const filtered = products.filter(product => {
        const searchTerm = searchFromUrl.toLowerCase();
        return (
          product.name?.toLowerCase().includes(searchTerm) ||
          product.description?.toLowerCase().includes(searchTerm) ||
          product.category?.toLowerCase().includes(searchTerm)
        );
      });
      setFilteredProducts(filtered);
    }
  }, [location.search, products]);

  // Fetch products and categories from Supabase
  useEffect(() => {
    // Scroll to top when component mounts or category changes
    window.scrollTo(0, 0);
    
    const fetchData = async () => {
      try {
        // Fetch products
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*')
          .eq('status', 'active')
          .order('created_at', { ascending: false });

        if (productsError) {
          console.error('Error fetching products:', productsError);
        } else {
          setProducts(productsData || []);
          setFilteredProducts(productsData || []);
        }

        // Fetch categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .eq('status', 'active')
          .order('name', { ascending: true });

        if (categoriesError) {
          console.error('Error fetching categories:', categoriesError);
        } else {
          console.log('Categories loaded:', categoriesData);
          setCategories(categoriesData || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter and sort products
  useEffect(() => {
    let filtered = [...products];

    // Get search query and subcategory from URL
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search');
    const subcategoryFromUrl = searchParams.get('subcategory');

    // Filter by country availability only (show all products available in the country)
    if (currency) {
      let targetCountry = '';
      if (currency === '$') targetCountry = 'USA';
      else if (currency === '£') targetCountry = 'UK';
      else if (currency === 'AED') targetCountry = 'UAE';
      else if (currency === 'KSA') targetCountry = 'Saudi Arabia';
      
      if (targetCountry) {
        filtered = filtered.filter(product => {
          // Check if product is available in the selected country
          const countries = product.countries || [];
          const isAvailableInCountry = countries.includes(targetCountry);
          
          console.log(`Product "${product.name}" countries: [${countries.join(', ')}], Target: ${targetCountry}, Available: ${isAvailableInCountry}`);
          return isAvailableInCountry;
        });
        console.log(`Filtered to ${filtered.length} products available in ${targetCountry}`);
      }
    }

    // Filter by search query
    if (searchQuery) {
      const searchTerm = searchQuery.toLowerCase();
      filtered = filtered.filter(product => {
        return (
          product.name?.toLowerCase().includes(searchTerm) ||
          product.description?.toLowerCase().includes(searchTerm) ||
          product.category?.toLowerCase().includes(searchTerm)
        );
      });
    }

    // Filter by category (only if no search query)
    if (selectedCategory !== 'all' && !searchQuery) {
      console.log('Filtering by category:', selectedCategory);
      console.log('All products categories:', products.map(p => p.category));
      
      // Show all products with their categories
      products.forEach(product => {
        console.log(`Product: "${product.name}" | Category: "${product.category}"`);
      });
      
      filtered = filtered.filter(product => {
        const productCategory = product.category?.toLowerCase().trim();
        const selectedCat = selectedCategory.toLowerCase().trim();
        
        // Exact match
        let matches = productCategory === selectedCat;
        
        // If no exact match, try partial matching for similar categories
        if (!matches) {
          // Handle variations like "Pet Care" vs "Pet Supplies"
          if (selectedCat.includes('pet') || productCategory.includes('pet')) {
            matches = selectedCat.includes('pet') && productCategory.includes('pet');
          }
          // Handle variations like "Home & Kitchen" vs "Home and Kitchen"
          else if (selectedCat.includes('home') || productCategory.includes('home')) {
            matches = selectedCat.includes('home') && productCategory.includes('home');
          }
          // Handle variations like "Personal Care" vs "Personal"
          else if (selectedCat.includes('personal') || productCategory.includes('personal')) {
            matches = selectedCat.includes('personal') && productCategory.includes('personal');
          }
          // Handle variations like "Electronics" vs "Electronic"
          else if (selectedCat.includes('electronic') || productCategory.includes('electronic')) {
            matches = selectedCat.includes('electronic') && productCategory.includes('electronic');
          }
        }
        
        if (!matches) {
          console.log(`❌ Product "${product.name}" category "${productCategory}" doesn't match "${selectedCat}"`);
        } else {
          console.log(`✅ Product "${product.name}" category "${productCategory}" matches "${selectedCat}"`);
        }
        return matches;
      });
      console.log(`Found ${filtered.length} products in category "${selectedCategory}"`);
    }

    // Filter by subcategory (only if no search query and subcategory is specified)
    if (selectedSubcategory && !searchQuery) {
      console.log('Filtering by subcategory:', selectedSubcategory);
      
      filtered = filtered.filter(product => {
        try {
          // Parse subcategory JSON if it's a string, otherwise use as is
          const productSubcategories = product.subcategory ? 
            (typeof product.subcategory === 'string' ? JSON.parse(product.subcategory) : product.subcategory) : 
            [];
          
          const selectedSubcat = selectedSubcategory.toLowerCase().trim();
          
          // Check if any of the product's subcategories match the selected subcategory
          const matches = productSubcategories.some(subcat => 
            subcat.toLowerCase().trim() === selectedSubcat
          );
          
          if (!matches) {
            console.log(`❌ Product "${product.name}" subcategories "${productSubcategories}" don't match "${selectedSubcat}"`);
          } else {
            console.log(`✅ Product "${product.name}" subcategories "${productSubcategories}" match "${selectedSubcat}"`);
          }
          return matches;
        } catch (e) {
          console.warn('Error parsing subcategory JSON for product:', product.name, e);
          return false;
        }
      });
      console.log(`Found ${filtered.length} products in subcategory "${selectedSubcategory}"`);
    }

    // Filter by price range
    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(Number);
      filtered = filtered.filter(product => {
        const price = parseFloat(getPriceForCurrency(product, 'current'));
        if (priceRange === '500+') {
          return price >= 500;
        }
        return price >= min && price <= max;
      });
    }

    // Sort products
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => parseFloat(getPriceForCurrency(a, 'current')) - parseFloat(getPriceForCurrency(b, 'current')));
        break;
      case 'price-high':
        filtered.sort((a, b) => parseFloat(getPriceForCurrency(b, 'current')) - parseFloat(getPriceForCurrency(a, 'current')));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'featured':
      default:
        // Keep original order (most recent first)
        break;
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, selectedSubcategory, priceRange, sortBy, location.search, currency]);



  return (
    <div className="products-page">
      <div className="products-hero">
        <div className="products-hero-content">
          {selectedSubcategory ? (
            <>
              <h1>{selectedSubcategory}</h1>
              <p>Products in {selectedSubcategory} category</p>
            </>
          ) : selectedCategory !== 'all' ? (
            <>
              <h1>{selectedCategory}</h1>
              <p>Products in {selectedCategory} category</p>
            </>
          ) : (
            <>
              <h1>All Products</h1>
              <p>Discover amazing products at unbeatable prices</p>
            </>
          )}
        </div>
      </div>

      {/* Search Results Display */}
      {new URLSearchParams(location.search).get('search') && (
        <div className="search-results-header">
          <div className="search-results-container">
            <h2>Search Results for "{new URLSearchParams(location.search).get('search')}"</h2>
            <p>{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found</p>
            <button 
              onClick={() => navigate('/products')}
              className="clear-search-btn"
            >
              Clear Search
            </button>
          </div>
        </div>
      )}

      {/* Advanced Filter Bar */}
      <div className="advanced-search-bar">
        <div className="search-container">
          <div className="filter-controls">
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Categories</option>
              {categories.length > 0 ? (
                categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))
              ) : (
                <option value="" disabled>Loading categories...</option>
              )}
            </select>



            <select 
              value={priceRange} 
              onChange={(e) => setPriceRange(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Prices</option>
              <option value="0-50">Under {getCurrencySymbol()}50</option>
              <option value="50-100">{getCurrencySymbol()}50 - {getCurrencySymbol()}100</option>
              <option value="100-200">{getCurrencySymbol()}100 - {getCurrencySymbol()}200</option>
              <option value="200-500">{getCurrencySymbol()}200 - {getCurrencySymbol()}500</option>
              <option value="500+">Over {getCurrencySymbol()}500</option>
            </select>

            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="featured">Featured</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>

              <option value="newest">Newest First</option>
            </select>
          </div>
        </div>
      </div>

      <div className="products-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-products">
            <p>No products found matching your criteria.</p>
            <button 
              onClick={() => {
                setSelectedCategory('all');
                setPriceRange('all');
                setSortBy('featured');
              }}
              className="reset-filters-btn"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="products-grid">
            {filteredProducts.map(product => (
              <div key={product.id} className="product-card-new">
                <Link to={`/product/${product.id}`} className="product-card-link">
                  <div className="product-card-image">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} loading="lazy" />
                    ) : (
                      <div className="product-placeholder-new">
                        <span>📦</span>
                      </div>
                    )}
                    <div className="product-overlay-new">
                      <div className="product-badges">
                        <span className="badge-featured">Featured</span>
                        {product.category && (
                          <span className="badge-category">{product.category}</span>
                        )}
                      </div>
                      <div className="product-actions-overlay">
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addToCart({
                              id: product.id,
                              name: product.name,
                              price: parseFloat(getPriceForCurrency(product, 'current')),
                              image: product.image_url,
                              vendor_id: product.vendor_id
                            });
                          }} 
                          className="quick-add-btn"
                          title="Add to Cart"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                          </svg>
                        </button>
                        <div className="quick-view-btn" title="View Details">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="product-card-content">
                    <div className="product-header-new">
                      <h3 className="product-title">{product.name}</h3>

                    </div>
                    <p className="product-description-new">
                      {product.description ? 
                        (product.description.length > 60 ? 
                          `${product.description.substring(0, 60)}...` : 
                          product.description
                        ) : 
                        'Amazing product with great features'
                      }
                    </p>
                    <div className="product-footer-new">
                      <div className="price-section-new">
                        <span className="current-price-new">{getCurrencySymbol()}{parseFloat(getPriceForCurrency(product, 'current')).toFixed(2)}</span>
                        <span className="original-price-new">{getCurrencySymbol()}{parseFloat(getPriceForCurrency(product, 'original')).toFixed(2)}</span>
                        <span className="discount-badge-new">-20%</span>
                      </div>
                    </div>
                  </div>
                </Link>
                <button 
                  onClick={() => addToCart({
                    id: product.id,
                    name: product.name,
                    price: parseFloat(getPriceForCurrency(product, 'current')),
                    image: product.image_url,
                    vendor_id: product.vendor_id
                  })}
                  className="add-to-cart-btn-new"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                  Add to Cart
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Products; 